import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Film, Loader2 } from 'lucide-react';
import { api } from '@watchparty/shared';
import { tmdbImage, workTypeLabel } from '@/lib/utils';
const FILTER_OPTIONS = [
    { id: 'all', label: 'All' },
    { id: 'movie', label: 'Movies' },
    { id: 'tv', label: 'TV' },
];
const PAGE_SIZE = 24;
// ---------------------------------------------------------------------------
// BrowseCard
// ---------------------------------------------------------------------------
function BrowseCard({ work }) {
    return (_jsx(Link, { to: "/works/$workId", params: { workId: work.workId }, style: { textDecoration: 'none' }, children: _jsxs("div", { children: [_jsx("div", { style: {
                        aspectRatio: '2/3',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                        background: 'var(--color-surface-sunken)',
                    }, children: work.posterUrl ? (_jsx("img", { src: tmdbImage(work.posterUrl, 'w342'), alt: work.title, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Film, { size: 28, style: { color: 'var(--color-text-faint)' } }) })) }), _jsx("p", { style: {
                        margin: '6px 0 0',
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.8125rem', fontWeight: 700,
                        color: 'var(--color-text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }, children: work.title }), _jsx("p", { style: {
                        margin: '1px 0 0',
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.7rem',
                        color: 'var(--color-text-muted)',
                    }, children: [work.releaseYear, workTypeLabel(work.workType)].filter(Boolean).join(' · ') })] }) }));
}
// ---------------------------------------------------------------------------
// DiscoverPage
// ---------------------------------------------------------------------------
export function DiscoverPage() {
    const navigate = useNavigate();
    const search = useSearch({ strict: false });
    const sentinelRef = useRef(null);
    const typeParam = search.type;
    const mediaType = typeParam === 'movie' || typeParam === 'tv' ? typeParam : 'all';
    const apiMediaType = mediaType === 'all' ? undefined : mediaType;
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, } = useInfiniteQuery({
        queryKey: ['works', 'popular', mediaType],
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
            if (apiMediaType)
                params.set('mediaType', apiMediaType);
            return api.get(`/works/popular?${params}`);
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    });
    const works = data?.pages.flatMap((p) => p.items) ?? [];
    const totalItems = data?.pages[0]?.totalItems ?? 0;
    // IntersectionObserver for infinite scroll
    const handleIntersect = useCallback((entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(handleIntersect, {
            rootMargin: '400px',
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [handleIntersect]);
    function handleFilterChange(filter) {
        const next = {};
        if (filter !== 'all')
            next.type = filter;
        navigate({ to: '/discover', search: next, replace: true });
    }
    return (_jsxs("div", { style: { maxWidth: '1000px', margin: '0 auto', padding: '24px 16px' }, children: [_jsx("h1", { style: {
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    marginBottom: '24px',
                }, children: "Browse" }), _jsxs("div", { style: { display: 'flex', gap: '0', marginBottom: '24px' }, children: [FILTER_OPTIONS.map((opt, i) => {
                        const isActive = mediaType === opt.id;
                        return (_jsx("button", { onClick: () => handleFilterChange(opt.id), style: {
                                padding: '8px 20px',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                border: '1px solid var(--color-border)',
                                cursor: 'pointer',
                                transition: 'color 200ms, background-color 200ms',
                                background: isActive ? 'var(--color-accent)' : 'var(--color-surface)',
                                color: isActive ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                                borderRadius: i === 0
                                    ? 'var(--radius-md) 0 0 var(--radius-md)'
                                    : i === FILTER_OPTIONS.length - 1
                                        ? '0 var(--radius-md) var(--radius-md) 0'
                                        : '0',
                                marginLeft: i > 0 ? '-1px' : '0',
                            }, children: opt.label }, opt.id));
                    }), totalItems > 0 && !isLoading && (_jsxs("span", { style: {
                            marginLeft: '16px',
                            alignSelf: 'center',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-faint)',
                        }, children: [totalItems.toLocaleString(), " titles"] }))] }), isLoading ? (_jsxs("div", { className: "watchlist-grid", children: [Array.from({ length: PAGE_SIZE }).map((_, i) => (_jsx("div", { children: _jsx("div", { style: {
                                aspectRatio: '2/3',
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--color-surface-sunken)',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            } }) }, i))), _jsx("style", { children: `@keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.5 } }` })] })) : works.length > 0 ? (_jsx("div", { className: "watchlist-grid", children: works.map((work) => (_jsx(BrowseCard, { work: work }, work.workId))) })) : (_jsxs("div", { style: { textAlign: 'center', padding: '64px 0', color: 'var(--color-text-faint)' }, children: [_jsx(Film, { size: 36, style: { opacity: 0.3, marginBottom: '12px' } }), _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem' }, children: "No works found." })] })), _jsx("div", { ref: sentinelRef, style: { height: '1px' } }), isFetchingNextPage && (_jsxs("div", { style: {
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '32px 0',
                }, children: [_jsx(Loader2, { size: 24, style: {
                            color: 'var(--color-text-faint)',
                            animation: 'spin 1s linear infinite',
                        } }), _jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` })] }))] }));
}
